import { AxiosInstance } from 'axios';

import { IAppContext } from '@byted-cg/linkchat-core';
import { I18n } from '../../utils/lang';
import { EventCode } from '../../constants/eventCode';
import {
  DIALOG_STATUS,
  DIALOG_TYPE,
  TRANSFER_TYPE,
} from '../../constants/status';
import createApis, { ApiTypes } from '../../service';
import store from '../../store/cache';
import createRequest, { Interceptors } from '../../utils/createRequest';
import queue from '../../lib/queue';
import { IJarvisCardInfo, IMessageContent } from '../../lib/sendMessage';
import {
  CombineMessageType,
  ICreateDialogProps,
  ITransferPeopleDmcBody,
  ITransferProps,
} from '../../lib/type';
import { logger } from '../metrics';
import { HttpInfo } from '../../constants/metrics';
import { DialogCreateResultType } from '../../constants';

interface HttpBasicProps {
  debug?: boolean;
  env?: string;
  createDialogExt?: {
    [key: string]: string;
  };
  appContext: IAppContext<CombineMessageType>;
}

interface HttpFullProps extends HttpBasicProps {
  request: {
    instance: AxiosInstance;
    baseOptions?: any;
    interceptors: Interceptors;
  };
}

class Http {
  public request: any;
  get store() {
    return store.getMap();
  }
  public apis: ApiTypes;
  private createDialogExt: {
    [key: string]: string;
  };
  private appContext: IAppContext<CombineMessageType, any>;
  private _cache: {
    createDialogParams: ICreateDialogProps;
  } = {
    createDialogParams: {},
  };

  constructor({
    createDialogExt,
    appContext,
    request: {
      instance: requestInstance,
      baseOptions: requestBaseOptions = {},
      interceptors: requestInterceptors = {},
    },
  }: HttpFullProps) {
    this.request = createRequest(requestInstance, {
      interceptors: {
        ...requestInterceptors,
      },
      baseOptions: requestBaseOptions,
    });
    this.apis = createApis(this.request);
    this.createDialogExt = createDialogExt || {};
    this.appContext = appContext;
  }

  /**
   * @description:
   * @param {ICreateDialogProps} params
   * @return {Promise}
   * @author: zhushitao
   */
  public createDialog = (params?: ICreateDialogProps) => {
    logger.info(HttpInfo.CreateDialog, params);
    if (params) {
      /** 缓存当前创建会话参数 for extMap */
      this._cache.createDialogParams = params;
    }
    const channel = this.store.channel;
    const {
      conversationTitle,
      openId,
      referenceUserType,
      uid,
      uname,
      useDmc,
      featureKey,
    } = this.appContext?.query || {};
    const { userId, businessLineId } = this.appContext?.userInfo || {};

    return this.apis
      .createDialog({
        openId,
        businessLineName: this.appContext?.userInfo.businessLineName,
        secretFromUserId: decodeURIComponent(
          this.appContext?.userInfo.secretUserId,
        ),
        referenceUserId:
          this.appContext?.userInfo?.referenceUserId &&
          decodeURIComponent(this.appContext?.userInfo?.referenceUserId),
        businessLineId: businessLineId,
        channel: typeof channel === 'undefined' ? 1 : channel,
        entryId: this.store.entryId,
        channelId: this.store.entryId,
        mobile: this.store.mobile,
        fromUserId: uid || userId || '0',
        fromUserName: uname || this.store.userName,
        referenceUserType,
        referenceExtraMap: JSON.stringify(this._cache.createDialogParams?.ext),
        conversationTitle: conversationTitle || this.store.userName, //Compatible with C-terminal
        useDmc: useDmc,
        externalMessageSummary: params?.externalMessage,
        skillId: params?.skillId,
        lang: this.store.lang,
        timeZone: this.store.timeZone,
        featureKey: featureKey,
      })
      .then(async (res) => {
        const { resultType, dialogVo, offlineFormVo } = res?.data || {};

        if (dialogVo) {
          this.appContext.dialogInfo = dialogVo;
        }

        const returnData = {
          dialog: dialogVo,
          type: resultType,
          offlineForm: offlineFormVo,
        };

        if (resultType === DialogCreateResultType.OFFLINE_FORM) {
          // 如果接口返回为留言表单直接不初始化长链

          await this.appContext.utils.jumpToOfflineForm(this.appContext, {
            useDmc: dialogVo?.useDmc,
            jarvisId: offlineFormVo.jarvisComponentId,
          });
        }
        // this.hasCreateDialog = true;
        return returnData;
      });
  };

  /**
   * @description: If there is currently no session, create a session directly. If there is a call, transfer to human
   * @param {ITransferProps} params
   * @return Promise
   * @author: zhushitao.
   */
  public transferAgent = (params?: ITransferProps) => {
    logger.info(HttpInfo.TransferAgent, {
      params: params,
    });
    if (
      !this.appContext.dialogInfo?.id ||
      Number(this.appContext.dialog?.ext?.status) === DIALOG_STATUS.ENDING
    ) {
      return this.createDialog({
        ...params,
        ext: this.createDialogExt,
      });
    } else {
      return this.apis
        .transferPeopleApi({
          id: this.appContext.dialog?.ext?.dialogId,
          transferType: params?.type || TRANSFER_TYPE.ROBOT_TO_AGENT, // Robot to manual type
          secretFromUserId: decodeURIComponent(
            this.appContext?.userInfo?.secretUserId,
          ),
          skillId: params?.skillId,
          ...params,
        })
        .then((res) => {
          this.appContext.dialogInfo = {
            ...this.appContext?.dialogInfo,
            ...res.data,
          };
          if (res && res.data?.transferJumpType === 1) {
            return {
              type: DIALOG_TYPE.OFFLINE, //Send a message
              ...res,
            };
          }
          return res;
        });
    }
  };

  /**
   * @description: If there is currently no session, create a session directly. If there is a call, transfer to human
   * @param {ITransferProps} params
   * @return Promise
   * @author: zhushitao.
   */
  public transferAgentWithDmc = (params?: ITransferPeopleDmcBody) => {
    logger.info(HttpInfo.TransferAgent, {
      params: params,
    });
    if (
      !this.appContext.dialogInfo?.id ||
      Number(this.appContext.dialog?.ext?.status) === DIALOG_STATUS.ENDING
    ) {
      return this.createDialog({
        ext: this.createDialogExt,
      });
    } else {
      return this.apis
        .transferPeopleDmcApi({
          id: this.appContext.dialog?.ext?.dialogId,
          transferType: TRANSFER_TYPE.ROBOT_TO_AGENT, // Robot to manual type
          secretFromUserId: decodeURIComponent(
            this.appContext?.userInfo?.secretUserId,
          ),
          ...params,
        })
        .then((res) => {
          this.appContext.dialogInfo = {
            ...this.appContext?.dialogInfo,
            ...res.data?.transferDialogInfo,
          };
          return res;
        });
    }
  };

  /**
   * @description Turn manual without bots go directly to create a session
   * @returns Promise
   */
  public transferAgentWithNoRobot = (params?: ITransferProps) => {
    logger.info(HttpInfo.TransferAgent, {
      params: params,
    });
    return this.createDialog({
      ...params,
      ext: this.createDialogExt,
    });
  };

  /**
   * @description: If there is currently no session, create a session directly. If there is a call, transfer to human
   * @param {ITransferProps} params
   * @return Promise
   * @author: zhushitao.
   */
  public transferAgentSimply = (params?: ITransferProps) => {
    logger.info(HttpInfo.TransferAgent, {
      params: params,
    });
    return this.apis
      .transferPeopleApi({
        id: this.appContext.dialog?.ext?.dialogId,
        transferType: params?.type || TRANSFER_TYPE.ROBOT_TO_AGENT, // Robot to manual type
        secretFromUserId: decodeURIComponent(
          this.appContext?.userInfo?.secretUserId,
        ),
        skillId: params?.skillId,
        ...params,
      })
      .then((res) => {
        this.appContext.dialogInfo = {
          ...this.appContext?.dialogInfo,
          ...res.data,
        };
        if (res && res.data?.transferJumpType === 1) {
          return {
            type: DIALOG_TYPE.OFFLINE, //Send a message
            ...res,
          };
        }
        return res;
      });
  };

  /**
   * @description:
   * @param {void}
   * @return {Promise}
   * @author: zhushitao
   */
  public cancelQueue = () => {
    return queue.clearQueue(this.appContext);
  };

  /**
   * @description Continue to consult
   */
  public keepGuide = async (params: any) => {
    const result = this.apis.keepGuide({
      businessLineId: String(this.appContext.userInfo.businessLineId),
      dialogId: this.appContext.dialogInfo?.id,
      ...params,
    });
    return result;
  };

  /**
   * @description: 判断是否是人工会话 然后结束
   * @author: zhushitao
   */
  public closeAgentDialog = async () => {
    if (Number(this.appContext.dialog.ext?.type) === DIALOG_TYPE.AGENT) {
      return this.apis.deleteDialog({
        dialogId: Number(this.appContext.dialogInfo?.id),
        jarvisEventCode: this.appContext.ext?.[EventCode.EndDialogEventCode],
      });
    }
    throw new Error(
      I18n.t(
        'the_current_session_is_not_in_progress',
        {},
        '当前会话不在进行中',
      ),
    );
  };

  /**
   * @description: End all types of sessions api
   * @author: zhushitao
   */
  public closeDialog = (params: any) => {
    return this.apis.deleteDialog({
      dialogId: Number(this.appContext.dialogInfo?.id),
      jarvisEventCode: this.appContext.ext?.[EventCode.EndDialogEventCode],
      ...params,
    });
  };

  /**
   * @description: Send bot message
   * @author: zhushitao
   */
  public sendRobotMessage = async (params: IMessageContent) => {
    return this.apis.sendRobotMessage({
      dialogId: this.appContext.dialogInfo?.id,
      ...params,
    });
  };

  /**
   * @description: Send jarvisCard
   * @author: zhushitao
   */
  public sendJarvisCard = async (params: IJarvisCardInfo) => {
    return this.apis.sendJarvisCard({
      dialogId: this.appContext.dialogInfo?.id,
      ...params,
    });
  };

  /**
   * @description: Modify a message
   * @param  { serverId: string; extMap: Record < string, string >;}
   * @author: zhushitao
   */
  public modifyMessage = async (params: {
    serverId: string;
    extMap: Record<string, string>;
  }) => {
    return (
      params.serverId &&
      this.apis.modifyMessage({
        dialogId: this.appContext.dialogInfo?.id,
        ...params,
      })
    );
  };
}

export default Http;
